/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

// Component imports
import { Col, Form, Input, Row, Select, Typography } from 'antd';
import VariableSelectItem from '../../../UI/VariableSelectItem';

// Misc imports
import { ACTIVITY_TYPES, VARIABLE_TYPES } from '../../../../constants/main';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import groupBy from '../../../../utils/array/groupBy';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Paragraph, Text } = Typography;

/**
 * Util to calculate and set a value to the 'data.switchVariable' prop for Flow Control nodes
 * @param {Object} form the form instance
 * @param {Array} variables the list of variables
 */
const calculateSwitchVariable = (form, variables) => {
  const currentVariable = form.getFieldValue(['data', 'variableName']);

  // Find the variable in the list of variables
  const variable = variables.find(item => item.name === currentVariable);

  // Switch logic based on the variable type
  switch (variable.type) {
    case VARIABLE_TYPES.CALLBACK:
      form.setFieldValue(['data', 'switchVariable'], `httpStorage.${currentVariable}.${form.getFieldValue(['data', 'controlNodePropName'])}`);
      break;

    case VARIABLE_TYPES.QUESTION:
      form.setFieldValue(['data', 'switchVariable'], `survey.${currentVariable}.answer`);
      break;

    default:
      form.setFieldValue(['data', 'switchVariable'], currentVariable);
      break;
  }
};

const SwitchVariableNodeForm = ({ form }) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const [searchParams] = useSearchParams();
  const { currentBotLanguage } = useBotLanguage();

  const { data: activity } = getCurrentQueryState('fetchActivity', { orgId, tenantId, activityId: searchParams.get('activity'), currentBotLanguage }, !searchParams.get('activity'));
  const { data: storyVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  return (
    <Fragment>
      <Form.Item
        label={t('Control variable')}
        name={['data', 'variableName']} // Local field
        className="mb-4"
      >
        <Select
          showSearch
          allowClear
          placeholder={t('Select a variable')}
          size="large"
          onSelect={() => calculateSwitchVariable(form, storyVariables)}
          filterOption={(input, option) => {
            // Filter Option iterates through the options, find the current option to get the name and the description data
            const currentVariable = storyVariables.find((variable) => variable.name === option.key);
            return currentVariable?.name?.toLowerCase().includes(input.toLowerCase()) || currentVariable?.description?.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {
            Object.entries(groupBy(storyVariables?.filter((item, index, self) => self.findIndex(t => t.name === item.name) === index && item.name !== 'BotName'), 'type'))
              .filter(([key]) => [VARIABLE_TYPES.CALLBACK, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.SESSION, VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.CSAT, (activity?.type === ACTIVITY_TYPES.LIVECHAT_FLOW ? VARIABLE_TYPES.LIVECHAT : null)].includes(key)) // Keep only supported variable types
              .map(([groupName, items]) => (
                <Select.OptGroup key={groupName} label={t(groupName, { context: 'variable type' })}>
                  {
                    items.map(item => (
                      <Select.Option
                        key={item.name}
                        value={groupName === VARIABLE_TYPES.CALLBACK ? `${item.name}.ADD_PROPERTY_HERE` : item.name}
                        name={item.name}
                      >
                        <VariableSelectItem variable={item} />
                      </Select.Option>
                    ))
                  }
                </Select.OptGroup>
              ))
          }
        </Select>
      </Form.Item>

      <Form.Item
        name={['data', 'switchVariable']}
        hidden
      >
        <Input />
      </Form.Item>

      {
        // If the variable is a callback, display another input to select the callback node
        form.getFieldValue(['data', 'variableName']) && storyVariables.find(variable => variable.name === form.getFieldValue(['data', 'variableName']))?.type === VARIABLE_TYPES.CALLBACK ? (
          <Fragment>
            <Row>
              <Col span={24}>
                <Text>
                  {t('Callback property')}
                </Text>
              </Col>
              <Col span={24}>
                <Paragraph type="secondary">
                  {t('Enter the property that will be affected by the flow control.')}
                </Paragraph>
              </Col>
            </Row>

            <Form.Item
              name={['data', 'controlNodePropName']}
              rules={[{ required: true, message: t('Property name cannot be blank.') }]}
            >
              <Input
                placeholder={t('e.g. myProperty')}
                size="large"
                onChange={() => calculateSwitchVariable(form, storyVariables)}
              />
            </Form.Item>
          </Fragment>
        ) : null
      }
    </Fragment>
  );
};

SwitchVariableNodeForm.propTypes = {
  form: PropTypes.object.isRequired
};

export default SwitchVariableNodeForm;
